import type { GroupTalkParticipant, GroupTalkTemplateId } from "./groupTalk";
import type { GroupTalkSessionView, GroupTalkTurnView } from "./groupTalkView";

const GROUP_TALK_DRAFT_KEY = "wizmate.group-talk.draft.v1";
const GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX = "wizmate.group-talk.session.v1:";

export type GroupTalkDraft = {
  templateId: GroupTalkTemplateId;
  title: string;
  description: string;
  participants: GroupTalkParticipant[];
};

export type GroupTalkActiveSessionSnapshot = {
  sessionId: string;
  draft: GroupTalkDraft;
  session: GroupTalkSessionView;
  turns: GroupTalkTurnView[];
};

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isValidParticipant(value: unknown): value is GroupTalkParticipant {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<GroupTalkParticipant>;
  return (
    typeof item.participantId === "string" &&
    typeof item.memberId === "string" &&
    (typeof item.stanceId === "string" || item.stanceId === null) &&
    typeof item.fixed === "boolean"
  );
}

function isValidDraft(value: unknown): value is GroupTalkDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Partial<GroupTalkDraft>;
  return (
    typeof draft.templateId === "string" &&
    typeof draft.title === "string" &&
    typeof draft.description === "string" &&
    Array.isArray(draft.participants) &&
    draft.participants.every((participant) => isValidParticipant(participant))
  );
}

function activeSessionKey(sessionId: string) {
  return `${GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX}${sessionId}`;
}

export function loadGroupTalkDraft(): GroupTalkDraft | null {
  if (!canUseSessionStorage()) return null;
  const parsed = safeParseJson<unknown>(window.sessionStorage.getItem(GROUP_TALK_DRAFT_KEY));
  return isValidDraft(parsed) ? parsed : null;
}

export function saveGroupTalkDraft(draft: GroupTalkDraft) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(GROUP_TALK_DRAFT_KEY, JSON.stringify(draft));
}

export function clearGroupTalkDraft() {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(GROUP_TALK_DRAFT_KEY);
}

export function loadGroupTalkActiveSessionSnapshot(sessionId: string): GroupTalkActiveSessionSnapshot | null {
  if (!canUseSessionStorage()) return null;
  return safeParseJson<GroupTalkActiveSessionSnapshot>(window.sessionStorage.getItem(activeSessionKey(sessionId)));
}

export function saveGroupTalkActiveSessionSnapshot(snapshot: GroupTalkActiveSessionSnapshot) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(activeSessionKey(snapshot.sessionId), JSON.stringify(snapshot));
}

export function clearGroupTalkActiveSessionSnapshot(sessionId: string) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(activeSessionKey(sessionId));
}
